import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Tooltip } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { notify } from "../../../../../services/toast.config";

interface WebhookDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhookUrl?: string;
  webhookToken?: string;
  providerName?: string;
}

const WebhookDetailsModal: React.FC<WebhookDetailsModalProps> = ({
  isOpen,
  onClose,
  webhookUrl,
  webhookToken,
  providerName = "WhatsApp Provider",
}) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = (text: string, label: string) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => notify.success(`${label} copied to clipboard`))
      .catch(() => notify.error("Failed to copy"));
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.55)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9000,
        padding: 24,
        boxSizing: "border-box",
      }}>
      <div
        style={{
          background: "#ffffff",
          borderRadius: 16,
          width: "100%",
          maxWidth: 640,
          maxHeight: "90vh",
          overflow: "hidden",
          boxShadow:
            "0 24px 48px rgba(15,23,42,0.35), 0 0 0 1px rgba(148,163,184,0.18)",
          display: "flex",
          flexDirection: "column",
        }}>
        <div
          className="d-flex justify-content-between align-items-center px-4 py-3 border-bottom"
          style={{ background: "#f9fafb" }}>
          <div>
            <h5 className="mb-0 fw-semibold">Webhook Details</h5>
            <small className="text-muted">
              Connect {providerName} to your system.
            </small>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 22,
              lineHeight: 1,
              cursor: "pointer",
              color: "#6b7280",
            }}>
            ×
          </button>
        </div>

        <div
          style={{
            padding: "18px 24px",
            overflowY: "auto",
            flex: 1,
          }}>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Use this URL and verify token in your provider dashboard so incoming
            WhatsApp messages and status updates reach this system.
          </p>

          <div className="mb-3">
            <label className="form-label fw-semibold mb-1">Webhook URL</label>
            <div className="d-flex align-items-center gap-2">
              <code
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "#f3f4f6",
                  fontSize: 12,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={webhookUrl}>
                {webhookUrl || "-"}
              </code>
              <Tooltip title="Copy URL">
                <button
                  type="button"
                  className="btn btn-light btn-sm d-flex align-items-center"
                  onClick={() =>
                    webhookUrl && handleCopy(webhookUrl, "Webhook URL")
                  }>
                  <CopyOutlined style={{ fontSize: 14 }} />
                </button>
              </Tooltip>
            </div>
            <small className="text-muted">
              Paste this as the callback / webhook URL in your provider.
            </small>
          </div>

          <div className="mb-3">
            <label className="form-label fw-semibold mb-1">Verify Token</label>
            <div className="d-flex align-items-center gap-2">
              <code
                style={{
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: "#f3f4f6",
                  fontSize: 12,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={webhookToken}>
                {webhookToken || "-"}
              </code>
              <Tooltip title="Copy token">
                <button
                  type="button"
                  className="btn btn-light btn-sm d-flex align-items-center"
                  onClick={() =>
                    webhookToken && handleCopy(webhookToken, "Verify token")
                  }>
                  <CopyOutlined style={{ fontSize: 14 }} />
                </button>
              </Tooltip>
            </div>
            <small className="text-muted">
              Use this as verify token when your provider asks for it.
            </small>
          </div>

          <div
            className="mt-3 p-3 rounded"
            style={{ background: "#f9fafb", border: "1px dashed #e5e7eb" }}>
            <h6 className="fw-semibold mb-2" style={{ fontSize: 13 }}>
              Quick setup steps
            </h6>
            <ol
              className="mb-0"
              style={{ paddingLeft: 18, fontSize: 13, color: "#4b5563" }}>
              <li>Open your provider dashboard for this WhatsApp number.</li>
              <li>Find the webhook / callback URL settings.</li>
              <li>Paste the URL above and set the verify token.</li>
              <li>Enable events for message, status, and template updates.</li>
              <li>Save changes and send a test message to verify.</li>
            </ol>
          </div>
        </div>

        <div className="d-flex justify-content-end px-4 py-3 border-top">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default WebhookDetailsModal;
